import { useMemo, useState } from 'react'
import {
  useRaidPlanDeaths,
  useRaidPlanDeathSources,
} from 'app/frontend/base/config/queries'
import { useTeamPlanTimeline } from '../components/TeamPlanTimelineContext'
import {
  buildDeathMarkers,
  DEFAULT_DEATH_LIMIT,
  DeathMarker,
  MAX_DEATH_LIMIT,
} from '../utils/deathTimeline'

const SOURCE_STORAGE_KEY = 'raid-plan-deaths-source'
const LIMIT_STORAGE_KEY = 'raid-plan-deaths-limit'

export type DeathTimelineSource =
  | 'hidden'
  | '7'
  | '14'
  | 'season'
  | `event:${number}`

const sourceStorageKey = (encounterId: number, difficulty: string) =>
  `${SOURCE_STORAGE_KEY}:${encounterId}:${difficulty}`

const readSource = (storageKey: string): DeathTimelineSource => {
  if (typeof window === 'undefined') return 'hidden'
  const stored = localStorage.getItem(storageKey)
  if (
    stored === '7' ||
    stored === '14' ||
    stored === 'season' ||
    stored?.startsWith('event:')
  ) {
    return stored as DeathTimelineSource
  }
  return 'hidden'
}

const readLimit = () => {
  if (typeof window === 'undefined') return DEFAULT_DEATH_LIMIT
  const parsed = Number(localStorage.getItem(LIMIT_STORAGE_KEY))
  if (!Number.isInteger(parsed)) return DEFAULT_DEATH_LIMIT
  return Math.min(MAX_DEATH_LIMIT, Math.max(1, parsed))
}

const sourceParam = (source: DeathTimelineSource) => {
  if (source === 'hidden') return undefined
  return source.startsWith('event:') ? source.slice('event:'.length) : source
}

const useDeathTimeline = (sourcesEnabled: boolean) => {
  const { owner, raidPlan, computed } = useTeamPlanTimeline()
  const teamId = owner.ownerType === 'team' ? owner.ownerId : undefined
  const storageKey = sourceStorageKey(raidPlan.encounterId, raidPlan.difficulty)
  const [storedSource, setStoredSource] = useState(() => ({
    storageKey,
    source: readSource(storageKey),
  }))
  if (storedSource.storageKey !== storageKey) {
    setStoredSource({ storageKey, source: readSource(storageKey) })
  }
  const source =
    storedSource.storageKey === storageKey
      ? storedSource.source
      : readSource(storageKey)
  const [limit, setLimitState] = useState(readLimit)
  const active = source !== 'hidden' && teamId != null

  const sourcesQuery = useRaidPlanDeathSources(
    teamId,
    raidPlan.encounterId,
    raidPlan.difficulty,
    sourcesEnabled && teamId != null,
  )
  const deathsQuery = useRaidPlanDeaths(
    teamId,
    raidPlan.encounterId,
    raidPlan.difficulty,
    sourceParam(source),
    active,
  )

  const markers = useMemo<DeathMarker[]>(() => {
    if (!active || !deathsQuery.data) return []
    return buildDeathMarkers({
      pulls: deathsQuery.data.pulls,
      phases: computed.phases,
      phaseStarts: computed.phaseStarts,
      effectivePhaseDurations: computed.effectivePhaseDurations,
      limit,
    })
  }, [
    active,
    computed.effectivePhaseDurations,
    computed.phaseStarts,
    computed.phases,
    deathsQuery.data,
    limit,
  ])

  const setSource = (next: DeathTimelineSource) => {
    setStoredSource({ storageKey, source: next })
    localStorage.setItem(storageKey, next)
  }

  const setLimit = (next: number) => {
    const clamped = Math.min(MAX_DEATH_LIMIT, Math.max(1, Math.round(next)))
    setLimitState(clamped)
    localStorage.setItem(LIMIT_STORAGE_KEY, String(clamped))
  }

  return {
    available: teamId != null,
    source,
    setSource,
    limit,
    setLimit,
    sources: sourcesQuery.data,
    sourcesLoading: sourcesQuery.isLoading,
    isLoading: active && deathsQuery.isLoading,
    pullCount: deathsQuery.data?.pullCount ?? 0,
    markers,
    active,
  }
}

export default useDeathTimeline
